import type { components } from '@pba/types-openapi-node';
import { parseISO } from 'date-fns';

export type NodeCesGroupPersonnelMembership = {
  createdAt?: Date;
  id: string;
  ref: string;
  role: NodePersonnelMembershipRole;
  type: 'group.cesGroup';
};

export type NodeCohortPersonnelMembership = {
  createdAt?: Date;
  id: string;
  ref: string;
  role: NodePersonnelMembershipRole;
  type: 'group.cohort';
};

export type NodeMedicalGroupPersonnelMembership = {
  createdAt?: Date;
  id: string;
  ref: string;
  role: NodePersonnelMembershipRole;
  type: 'group.medicalGroup';
};

export type NodeMembershipType = components['schemas']['Personnel']['memberships'][number]['type'];

export type NodePersonnel = {
  createdAt: string;
  email?: string;
  firstName: string;
  id: string;
  lastName: string;
  licenses?: NodePersonnelLicense[];
  memberships: NodePersonnelMembership[];
  providerType?: NodePersonnelProviderType;
  status: NodePersonnelStatus;
  updatedAt: string;
  user: string;
};

export type NodePersonnelLicense = {
  country: string;
  id: string;
  identifiers?: Record<string, string>;
  state?: string;
  type: string;
  validFrom?: string;
  validTo?: string;
};

export type NodePersonnelMembership =
  | NodeCesGroupPersonnelMembership
  | NodeCohortPersonnelMembership
  | NodeMedicalGroupPersonnelMembership;

export type NodePersonnelMembershipRole = components['schemas']['Personnel']['memberships'][number]['role'];

export type NodePersonnelProviderType = components['schemas']['Personnel']['providerType'];

export type NodePersonnelStatus = components['schemas']['Personnel']['status'];

export const createNodePersonnelMembership = (
  membership: components['schemas']['Personnel']['memberships'][number],
): NodePersonnelMembership => ({
  createdAt: membership.createdAt ? parseISO(membership.createdAt) : undefined,
  id: membership._id,
  ref: membership.ref,
  role: membership.role,
  type: membership.type,
});

export const createNodePersonnelLicense = (
  license: NonNullable<components['schemas']['Personnel']['licenses']>[number],
): NodePersonnelLicense => ({
  country: license.country,
  id: license._id,
  identifiers: license.identifiers,
  state: license.state,
  type: license.type,
  validFrom: license.validFrom,
  validTo: license.validTo,
});

export const createNodePersonnel = (personnel: components['schemas']['Personnel']): NodePersonnel => ({
  createdAt: personnel.createdAt,
  email: personnel.email,
  firstName: personnel.firstName,
  id: personnel._id,
  lastName: personnel.lastName,
  licenses: personnel.licenses?.map((license) => createNodePersonnelLicense(license)),
  memberships: personnel.memberships.map((membership) => createNodePersonnelMembership(membership)),
  providerType: personnel.providerType,
  status: personnel.status,
  updatedAt: personnel.updatedAt,
  user: personnel.user,
});
